import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPressReleaseData, getMediaCoverageData, formatDate } from '../services/googleSheets';

interface NewsItem {
  id: number;
  작성일: string;
  제목: string;
  내용?: string;
  카테고리?: string;
  언론사?: string;
  [key: string]: any;
}

const HomePage = () => {
  const navigate = useNavigate();
  const [pressReleases, setPressReleases] = useState<NewsItem[]>([]);
  const [mediaCoverage, setMediaCoverage] = useState<NewsItem[]>([]);
  const [activeTab, setActiveTab] = useState('media');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [pressData, mediaData] = await Promise.all([
          getPressReleaseData(),
          getMediaCoverageData()
        ]);
        
        // 최신 3개만 가져오기
        setPressReleases(pressData.slice(0, 3) as NewsItem[]);
        setMediaCoverage(mediaData.slice(0, 3) as NewsItem[]);
      } catch (error) {
        console.error('데이터 로딩 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleNewsClick = (item: NewsItem) => {
    if (activeTab === 'press') {
      navigate(`/press-release/${item.id}`);
    } else {
      navigate(`/media-coverage/${item.id}`);
    }
  };

  const renderNewsItems = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          데이터를 불러오는 중...
        </div>
      );
    }

    const items = activeTab === 'press' ? pressReleases : mediaCoverage;
    
    if (items.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          등록된 게시물이 없습니다.
        </div>
      );
    }

    return items.map((item, index) => (
      <article key={index} className="news-item">
        <div className="news-date">{formatDate(item.작성일)}</div>
        <div className="news-category">{activeTab === 'press' ? '보도자료' : '언론보도'}</div>
        <h3>{item.제목}</h3>
        <p>{item.내용 ? item.내용.substring(0, 100) + '...' : '내용을 확인해보세요.'}</p>
        <button 
          className="news-detail-btn"
          onClick={() => handleNewsClick(item)}
          style={{
            background: 'transparent',
            color: '#28a745',
            border: '1px solid #e0e0e0',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontWeight: '600',
            cursor: 'pointer',
            marginTop: '1rem',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.3rem'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(40, 167, 69, 0.2)';
            e.currentTarget.style.borderColor = '#28a745';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.borderColor = '#e0e0e0';
          }}
        >
          자세히보기 <span>→</span>
        </button>
      </article>
    ));
  };

  return (
    <>
      {/* Hero Section - 영상 16:9 비율 */}
      <section style={{ 
        width: '100%',
        paddingTop: '56.25%', // 16:9 비율 (9/16 * 100)
        position: 'relative',
        background: '#000',
        overflow: 'hidden',
        marginTop: '80px' // 헤더 높이만큼 여백 추가
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%'
        }}>
          <iframe
            src="https://www.youtube.com/embed/Q5W9tPIV_3w?autoplay=1&mute=1&controls=0&showinfo=0&rel=0&loop=1&playlist=Q5W9tPIV_3w&modestbranding=1&fs=0&cc_load_policy=0&iv_load_policy=3&autohide=1&disablekb=1"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              pointerEvents: 'none'
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="스마트폰프리운동"
          />
        </div>
      </section>

      {/* 최신 소식 - 언론보도를 먼저 표시 */}
      <section className="news">
        <div className="container">
          <h2>최신 소식</h2>
          <div className="news-tabs">
            <button 
              className={`tab-btn ${activeTab === 'media' ? 'active' : ''}`}
              onClick={() => setActiveTab('media')}
            >
              📰 언론보도
            </button>
            <button 
              className={`tab-btn ${activeTab === 'press' ? 'active' : ''}`}
              onClick={() => setActiveTab('press')}
            >
              📢 보도자료
            </button>
          </div>
          <div className="news-grid">
            {renderNewsItems()}
          </div>
        </div>
      </section>

      {/* 주요 활동 - 주요활동 페이지 내용 반영 */}
      <section className="activities">
        <div className="container">
          <h2>주요 활동</h2>
          <div className="activities-grid">
            <div className="activity-card">
              <div className="activity-image">🎤</div>
              <h3>01 강연</h3>
              <p>학교, 기관, 단체를 대상으로 스마트폰 과의존 예방과 올바른 디지털 미디어 사용에 대한 강연을 진행하고 있습니다.</p>
              <a href="/activities" className="card-link">자세히 보기 →</a>
            </div>
            <div className="activity-card">
              <div className="activity-image">📢</div>
              <h3>02 캠페인</h3>
              <p>스마트폰 프리 챌린지, 디지털 디톡스 캠프, 가족과 함께하는 스마트폰 프리 데이 등 다양한 캠페인을 진행합니다.</p>
              <a href="/activities" className="card-link">자세히 보기 →</a>
            </div>
            <div className="activity-card">
              <div className="activity-image">📊</div>
              <h3>03 연구조사</h3>
              <p>청소년의 스마트폰 사용 실태와 영향에 대한 체계적인 연구조사를 수행하고 있습니다.</p>
              <a href="/activities" className="card-link">자세히 보기 →</a>
            </div>
            <div className="activity-card">
              <div className="activity-image">⚖️</div>
              <h3>04 입법</h3>
              <p>청소년의 건강한 디지털 생활을 위한 제도적 기반 마련을 위해 입법 활동을 진행하고 있습니다.</p>
              <a href="/activities" className="card-link">자세히 보기 →</a>
            </div>
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #e74c3c, #c0392b)',
            color: 'white',
            padding: '2rem',
            borderRadius: '12px',
            textAlign: 'center',
            marginTop: '3rem'
          }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>
              활동 문의
            </h3>
            <p style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
              모든 활동 관련 문의는 아래 이메일로 연락주시기 바랍니다.
            </p>
            <p style={{ 
              fontSize: '1.3rem', 
              fontWeight: 'bold'
            }}>
              📧 phonfreekr@gmail.com
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage; 